<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onMounted, ref } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAcesso } from 'dashboard/composables/useAcesso';

import AddAgent from './AddAgent.vue';
import EditAgent from './EditAgent.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showDeletePopup = ref(false);
const showEditPopup = ref(false);
const agentAPI = ref({ message: '' });
const currentAgent = ref({});
const searchQuery = ref('');

const deleteConfirmText = computed(
  () => `${t('AGENT_MGMT.DELETE.CONFIRM.YES')} ${currentAgent.value.name}`
);
const deleteRejectText = computed(() => {
  return `${t('AGENT_MGMT.DELETE.CONFIRM.NO')} ${currentAgent.value.name}`;
});
const deleteMessage = computed(() => {
  return ` ${currentAgent.value.name}?`;
});

const agentList = computed(() => getters['agents/getAgents'].value);

const filteredAgentList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return agentList.value;
  return picoSearch(agentList.value, query, ['name', 'email']);
});

const uiFlags = computed(() => getters['agents/getUIFlags'].value);
const currentUserId = computed(() => getters.getCurrentUserID.value);
// ACESSO-01 (L8b): com a chave ACESSO_GRANULAR ligada, editar e remover pessoas é do `people:manage` (`agents#update`,
// `#destroy`), e o servidor confere por cima que ninguém mexe em quem tem mais (`Acesso::Concessao`). Desligada, o
// papel, como abaixo.
const { decide, ligado } = useAcesso();

// Moderators may invite new members, but editing and removing existing ones stays with admins.
const canManageExistingAgents = computed(() =>
  decide('people:manage', () => getters.getCurrentRole.value !== 'moderator')
);

onMounted(() => {
  store.dispatch('agents/get');
  // A lista de papéis alimenta o <select> do EditAgent. Só administrador atribui, e só ele
  // consegue ler o endpoint — para os outros a chamada é negada e o campo nem aparece.
  // ACESSO-01 (L9b): com a chave ligada, o papel personalizado de hoje sai do modal, e o select único de papel lê os
  // papéis da conta (`SeletorDePapel`): a lista daqui não serve mais.
  if (!ligado.value && getters.getCurrentRole.value === 'administrator') {
    store.dispatch('customRoles/get');
  }
});

const getAgentRoleName = agent => {
  return t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`);
};

// ACESSO-01 (L9b): com a chave ligada, a coluna mostra o papel da pessoa, o modelo da Powerbizz ou o papel da conta
// (`agent.papel`, o mesmo do perfil), e as exceções dela, em texto. Desligada, o papel base, como hoje.
const nomeDoPapel = agent =>
  ligado.value ? agent.papel?.nome : getAgentRoleName(agent);

// ACESSO-01 (L11b, S5): o número conta as exceções em vigor, e a vencida, que já não vale, aparece à parte até alguém
// removê-la: "2 exceções", "1 exceção vencida" ou "2 exceções e 1 vencida". Sem nenhuma, nada.
const excecoesNaLista = agent => {
  const { excecoes_em_vigor: emVigor, excecoes_vencidas: vencidas } = agent;
  if (!ligado.value || !(emVigor || vencidas)) return '';
  if (!vencidas) {
    return t('AGENT_MGMT.ACESSO.EXCECOES_NA_LISTA', { n: emVigor });
  }
  if (!emVigor) {
    return t('AGENT_MGMT.ACESSO.VENCIDAS_NA_LISTA', { n: vencidas });
  }
  return t('AGENT_MGMT.ACESSO.EXCECOES_E_VENCIDAS_NA_LISTA', {
    excecoes: t('AGENT_MGMT.ACESSO.EXCECOES_NA_LISTA', { n: emVigor }),
    n: vencidas,
  });
};

const verifiedAdministrators = computed(() => {
  return agentList.value.filter(
    agent => agent.role === 'administrator' && agent.confirmed
  );
});

const showEditAction = agent => {
  return canManageExistingAgents.value && currentUserId.value !== agent.id;
};

const showDeleteAction = agent => {
  if (!canManageExistingAgents.value) {
    return false;
  }

  if (currentUserId.value === agent.id) {
    return false;
  }

  if (!agent.confirmed) {
    return true;
  }

  if (agent.role === 'administrator') {
    return verifiedAdministrators.value.length !== 1;
  }
  return true;
};

const showAlertMessage = message => {
  loading.value[currentAgent.value.id] = false;
  currentAgent.value = {};
  agentAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = agent => {
  showEditPopup.value = true;
  currentAgent.value = agent;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = agent => {
  showDeletePopup.value = true;
  currentAgent.value = agent;
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const deleteAgent = async id => {
  try {
    await store.dispatch('agents/delete', id);
    showAlertMessage(t('AGENT_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    showAlertMessage(t('AGENT_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const confirmDeletion = () => {
  loading.value[currentAgent.value.id] = true;
  closeDeletePopup();
  deleteAgent(currentAgent.value.id);
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AGENT_MGMT.LOADING')"
    :no-records-found="!agentList.length"
    :no-records-message="$t('AGENT_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('AGENT_MGMT.HEADER')"
        :description="$t('AGENT_MGMT.DESCRIPTION')"
        :link-text="$t('AGENT_MGMT.LEARN_MORE')"
        :search-placeholder="$t('AGENT_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="agents"
      >
        <template v-if="agentList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('AGENT_MGMT.COUNT', { n: agentList.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="$t('AGENT_MGMT.HEADER_BTN_TXT')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <span
        v-if="!filteredAgentList.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('AGENT_MGMT.NO_RESULTS') }}
      </span>
      <div v-else class="divide-y divide-n-weak">
        <div
          v-for="(agent, index) in filteredAgentList"
          :key="agent.email"
          class="flex justify-between flex-row items-start gap-4 py-4"
        >
          <!-- `min-w-0` na cadeia inteira: sem ele o flex não encolhe abaixo do conteúdo, e no
               celular a linha de detalhes passava da borda do card e empurrava os botões para
               fora da tela. -->
          <div class="flex items-center gap-4 min-w-0 flex-1">
            <Avatar
              :src="agent.thumbnail"
              :name="agent.name"
              :status="agent.availability_status"
              :size="40"
              hide-offline-status
            />
            <div class="flex flex-col gap-1.5 items-start min-w-0">
              <span class="block text-heading-3 text-n-slate-12 capitalize">
                {{ agent.name }}
              </span>
              <div
                class="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2 min-w-0"
              >
                <!-- O e-mail quebra depois do "@" quando não cabe (o <wbr> não entra no texto
                     copiado); `break-words` só corta no meio se nem o domínio couber. -->
                <span
                  class="text-body-main text-n-slate-11 break-words min-w-0"
                >
                  {{ `${agent.email.split('@')[0]}@` }}<wbr />{{
                    agent.email.split('@')[1]
                  }}
                </span>
                <div class="hidden sm:block w-px h-3 bg-n-strong rounded-lg" />
                <div class="flex items-center gap-2">
                  <span class="block w-fit text-body-main text-n-slate-11">
                    {{ nomeDoPapel(agent) }}
                  </span>
                  <template v-if="excecoesNaLista(agent)">
                    <div class="w-px h-3 bg-n-strong rounded-lg" />
                    <span
                      class="text-body-main text-n-slate-11"
                      data-excecoes-na-lista
                    >
                      {{ excecoesNaLista(agent) }}
                    </span>
                  </template>
                  <div class="w-px h-3 bg-n-strong rounded-lg" />
                  <span
                    v-if="agent.confirmed"
                    class="text-body-main text-n-slate-11"
                  >
                    {{ $t('AGENT_MGMT.LIST.VERIFIED') }}
                  </span>
                  <span
                    v-if="!agent.confirmed"
                    class="text-body-main text-n-slate-11"
                  >
                    {{ $t('AGENT_MGMT.LIST.VERIFICATION_PENDING') }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div class="flex justify-end gap-3 shrink-0">
            <Button
              v-if="showEditAction(agent)"
              v-tooltip.top="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
              icon="i-woot-edit-pen"
              slate
              sm
              @click="openEditPopup(agent)"
            />
            <Button
              v-if="showDeleteAction(agent)"
              v-tooltip.top="$t('AGENT_MGMT.DELETE.BUTTON_TEXT')"
              icon="i-woot-bin"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              :is-loading="loading[agent.id]"
              @click="openDeletePopup(agent, index)"
            />
          </div>
        </div>
      </div>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddAgent @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditAgent
        v-if="showEditPopup"
        :id="currentAgent.id"
        :name="currentAgent.name"
        :provider="currentAgent.provider"
        :type="currentAgent.role"
        :email="currentAgent.email"
        :availability="currentAgent.availability_status"
        :custom-role-id="currentAgent.custom_role_id"
        :papel="currentAgent.papel"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AGENT_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </SettingsLayout>
</template>
